import React, { useCallback, useLayoutEffect } from 'react';
import { Platform, View } from 'react-native';
import { format, parseISO } from 'date-fns';
import Animated from 'react-native-reanimated';
import Logo from 'design-system/Logo';
import Text from 'design-system/Text';
import CardRow from 'design-system/CardRow';
import InfoCard from 'design-system/InfoCard';
import Indicator from 'design-system/Indicator';
import BottomPanel from 'design-system/BottomPanel';
import SectionHeader from 'design-system/SectionHeader';
import CardUpgradeRow from 'design-system/CardUpgradeRow';
import CardRowContainer from 'design-system/CardRowContainer';
import CardRowTitleWithToolTip from 'design-system/CardRowTitleWithToolTip';
import { rem, SCREEN_BOTTOM_PADDING, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { AppView, Button, CardView, Separator, Spacer, withAnimatedNavigationHeader } from 'design-system';
import Links, { knownLinks } from 'utils/links';
import { formatAmount, getFractionDigits } from 'utils';
import useStyles from 'hooks/useStyles';
import { useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { selectIsUserUltimate } from 'features/premium/selectors/tier';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { formatNumberOfShares, getTradingAccountName } from '../utils';
import { useOrderPreview, useStockMeta } from '../hooks';
import { selectTradingAccountFromId } from '../reducers/selectors';
import { AccountTypeDisplayLong, commonToolTipStrings, SHARE_QUANTITY_DECIMALS } from '../constants';
import { isWeb } from '../../../constants';
const formatPriceUpdatedAt = (date) => format(parseISO(date), 'd MMM yyyy');
const SecclOrderPreviewScreen = withAnimatedNavigationHeader(({ scrollHandler, route, navigation }) => {
    const { orderPreview: cachedOrderPreview, onConfirm, assetId, tradingAccountId, orderType, accountType, depositMethod, } = route.params;
    const styles = useStyles(styleSet);
    const isBuy = orderType.type === 'buy';
    const stockCache = useStockMeta(assetId);
    const showUpgradeRow = !useAppSelector(selectIsUserUltimate);
    const tradingAccount = useAppSelector((store) => selectTradingAccountFromId(store, tradingAccountId));
    const assetType = useAppSelector((store) => store.invest.caches[assetId]?.type);
    const minimumTransferUnit = useAppSelector((store) => store.invest.caches[assetId]?.minimumTransferUnit);
    const min = assetType === 'ETF' ? 0.0001 : minimumTransferUnit;
    const maximumFractionDigits = min !== undefined ? getFractionDigits(min) : SHARE_QUANTITY_DECIMALS;
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
        });
    }, []);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const onPressTAndC = useCallback(() => {
        if (Platform.OS === 'android') {
            navigation.navigate('Pdf', { url: knownLinks.investTerms });
        }
        else {
            Links.openLink(knownLinks.investTerms);
        }
    }, []);
    const [fullOrderPreview] = useOrderPreview(assetId, tradingAccountId, orderType.type === 'buy'
        ? {
            operation: 'buy',
            initialAmount: orderType.amount.value,
            cachedOrderPreview,
            updateOnMount: !cachedOrderPreview,
            fundOrderWithOpenBanking: depositMethod === 'openBankingDeposit',
            fundOrderWithDirectDebit: depositMethod === 'singleDirectDebit',
        }
        : { operation: 'sell', initialAmount: orderType.quantity, cachedOrderPreview });
    const handleConfirm = useCallback(() => {
        if (fullOrderPreview) {
            onConfirm(fullOrderPreview.order);
        }
    }, [fullOrderPreview, onConfirm]);
    if (!fullOrderPreview) {
        return (<AppView withFrame={false} style={styles.container}>
          <Indicator dark/>
        </AppView>);
    }
    const { ongoingFee, transactionFee, incidentalFee, platformFee, totalFee, illustrativeValue } = fullOrderPreview.illustrationDetails || {};
    const { estimated, commission } = fullOrderPreview.order;
    const { priceTimestamp, quantity, amount: totalCost, price: lastClosedPrice } = estimated;
    const computedQuantity = formatNumberOfShares(quantity, false, undefined, undefined, isBuy ? SHARE_QUANTITY_DECIMALS : maximumFractionDigits);
    return (<AppView withFrame={false} style={styles.container}>
        <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={[paddingHorizontalStyle, styles.contentContainer]}>
          <View style={styles.headerContainer}>
            <View style={styles.row}>
              <View style={styles.textContainer}>
                <Text Text-20>
                  {isBuy ? 'Buy' : 'Sell'} {stockCache?.name}
                </Text>
                <Spacer h={8}/>
                <Text TextThin-14 Secondary>
                  Price updated on {formatPriceUpdatedAt(priceTimestamp)}
                </Text>
              </View>
              <Spacer w={16}/>
              <Logo R44 uri={stockCache?.iconUrl}/>
            </View>
          </View>
          <SectionHeader horizontalMargins largeVerticalMargins>
            Review {isBuy ? 'buy' : 'sell'} order
          </SectionHeader>
          <CardView noMarginTop noMarginBottom>
            <CardRowContainer>
              <CardRow small tableSpacing title="Account" info={accountType === 'JISA' ? getTradingAccountName(tradingAccount) : AccountTypeDisplayLong[accountType]}/>
              {isBuy ? (<CardRow small tableSpacing title="Amount" info={<Text Numbers-14>{formatAmount(totalCost.value, totalCost.currency, 2)}</Text>}/>) : null}
              <CardRow small tableSpacing title="Last close price" info={<Text Numbers-14>{formatAmount(lastClosedPrice.value, lastClosedPrice.currency, 2)}</Text>}/>
              <CardRow small tableSpacing info={<Text Numbers-14>{computedQuantity}</Text>} title={isBuy ? (<CardRowTitleWithToolTip title={'No. of shares (est.)'} toolTipTitle={commonToolTipStrings.noOfSharesTitle} toolTipDescription={commonToolTipStrings.noOfSharesWithoutFxFeeDesc}/>) : ('No. of shares')}/>
              <CardRow small tableSpacing title={<CardRowTitleWithToolTip title="Trading charge" toolTipTitle={commonToolTipStrings.tradingChargeTitle} toolTipDescription={commonToolTipStrings.tradingChargeDesc}/>} info={<Text Numbers-14>{formatAmount(commission.value, commission.currency, 2)}</Text>}/>
              <CardRow small tableSpacing title={<Text Text-14>{isBuy ? 'Total cost' : 'Total amount (est.)'}</Text>} info={<Text Numbers-14>{formatAmount(totalCost.value, totalCost.currency, 2)}</Text>}/>
            </CardRowContainer>
          </CardView>

          {isBuy ? (<>
              <SectionHeader horizontalMargins largeVerticalMargins>
                How much might this cost
              </SectionHeader>
              <CardView noMarginTop noMarginBottom>
                <View style={styles.descriptionContainer}>
                  <Text TextThin-14 Secondary>
                    If you have this investment for 5 years, you can expect the following charges over that 5 years,
                    assuming 5% annual growth.
                  </Text>
                </View>
                <Separator />
                <CardRowContainer>
                  <CardRow small tableSpacing title={<CardRowTitleWithToolTip title="Ongoing charges figure" toolTipTitle={commonToolTipStrings.ongoingChargeTitle} toolTipDescription={commonToolTipStrings.ongoingChargeDesc}/>} info={<Text Numbers-14>{formatAmount(ongoingFee, lastClosedPrice.currency, 2)}</Text>}/>
                  <CardRow small tableSpacing title={<CardRowTitleWithToolTip title="Transaction cost (est.)" toolTipTitle={commonToolTipStrings.transactionCostTitle} toolTipDescription={commonToolTipStrings.transactionCostDesc}/>} info={<Text Numbers-14>{formatAmount(transactionFee, lastClosedPrice.currency, 2)}</Text>}/>
                  <CardRow small tableSpacing title={<CardRowTitleWithToolTip title="Incidental cost (est.)" toolTipTitle={commonToolTipStrings.incidentalCostTitle} toolTipDescription={commonToolTipStrings.incidentalCostDesc}/>} info={<Text Numbers-14>{formatAmount(incidentalFee, lastClosedPrice.currency, 2)}</Text>}/>
                  <CardRow small tableSpacing title={<CardRowTitleWithToolTip title="AUM fee" toolTipTitle={commonToolTipStrings.platformFeeTitle} toolTipDescription={commonToolTipStrings.platformFeeDesc}/>} info={<Text Numbers-14>{formatAmount(platformFee, lastClosedPrice.currency, 2)}</Text>}/>
                  <CardRow small tableSpacing title="Total costs (est.)" info={<Text Numbers-14>{formatAmount(totalFee, 'GBP', 2)}</Text>}/>
                  <CardRow small tableSpacing title={<Text Text-14>Illustrative 5 year value</Text>} info={<Text Numbers-14>{formatAmount(illustrativeValue, lastClosedPrice.currency, 2)}</Text>}/>
                </CardRowContainer>
                {showUpgradeRow ? (<>
                    <CardUpgradeRow title="Want lower AUM fee?" withHorizontalMargins/>
                    <Spacer h={16}/>
                  </>) : null}
              </CardView>

              <Spacer h={16}/>

              <InfoCard>
                <Text TextThin-14 Secondary>
                  Costs and returns are illustrative and not guaranteed. Check investment charges before investing.
                  Figures may vary due to rounding or compounding. Your capital is at risk.
                </Text>
              </InfoCard>
            </>) : null}
        </Animated.ScrollView>
        <BottomPanel>
          <Text TextThin-14 Secondary centered>
            Our{' '}
            <Text Text-14 Primary suppressHighlighting onPress={onPressTAndC}>
              T&Cs
            </Text>{' '}
            apply to this transaction
          </Text>
          <Spacer h={16}/>
          <Button preventDoubleTap brand title="Confirm" onPress={handleConfirm}/>
        </BottomPanel>
      </AppView>);
}, ({ colors }) => ({
    title: '',
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets(() => ({
    container: {
        flex: 1,
    },
    headerContainer: {
        paddingTop: rem(8),
        paddingHorizontal: rem(16),
    },
    descriptionContainer: {
        padding: rem(16),
    },
    contentContainer: {
        paddingBottom: SCREEN_BOTTOM_PADDING,
    },
    row: {
        flexDirection: 'row',
    },
    textContainer: {
        flex: 1,
    },
}));
export default SecclOrderPreviewScreen;
